/** Store → product editor (new + existing): details, media, options → variants matrix, organisation, SEO. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Affix, Button, Card, Checkbox, Col, Collapse, Empty, Form, Input, InputNumber, Popconfirm, Result, Row, Select, Space, Spin,
    Typography, Upload, message,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, ExportOutlined, PictureOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { MediaPickerModal, reportStorageError } from './Media';
import ProductDigitalFiles from './ShopDigitalFiles';
import { useTranslation } from '../../../shared/i18n';
import TagsInput from '../../../shared/components/TagsInput';
import RichEditor from './RichEditor';
import CustomFieldsCard from './CustomFieldsCard';
import TranslationsCard from './TranslationsCard';
import ProductVariants, { MAX_VARIANTS, sig, toGrams, toUnit } from './ProductVariants';
import ShopAiButton, { mergeAi } from './ShopAiButton';
import { directUpload } from './api';
import { Hint, StatusTag, siteUrl, sitePath, slugify, toastErr, useLeaveGuard, useSite } from './common';
import { MoneyInput, useCollections, useDragList } from './ShopCommon';

const { Text } = Typography;

const newVariant = (base = {}) => ({
    price: 0, compare_at_price: null, cost: null, sku: '', barcode: '', weight: 0, requires_shipping: true, taxable: true,
    track_inventory: true, inventory_policy: 'deny', inventory_quantity: 0, ...base,
});

/** all combinations of option values, keeping existing variants (by option values) */
const buildVariants = (options, current) => {
    const opts = options.filter((o) => o.name && o.values?.length);
    if (!opts.length) {
        const first = current[0] || newVariant();
        return [{ ...first, options: [], title: 'Default' }];
    }
    let combos = [[]];
    opts.forEach((o) => { combos = combos.flatMap((c) => o.values.map((v) => [...c, v])); });
    combos = combos.slice(0, MAX_VARIANTS);
    const bySig = new Map(current.map((v) => [sig(v.options), v]));
    const tmpl = current[0] || newVariant();
    return combos.map((c) => {
        const ex = bySig.get(sig(c));
        if (ex) return ex;
        const { id, sku, barcode, inventory_quantity, image_id, ...rest } = tmpl; // eslint-disable-line no-unused-vars
        return newVariant({ ...rest, options: c, title: c.join(' / '), sku: '', inventory_quantity: 0 });
    });
};

const EMPTY = {
    title: '', handle: '', description_html: '', vendor: '', product_type: '', tax_class: 'standard', ships_in_days: null, tags: [], options: [], status: 'draft',
    seo: { title: '', description: '' }, variants: [newVariant({ options: [], title: 'Default' })], collection_ids: [], images: [],
};

const ProductEdit = () => {
    const { t } = useTranslation();
    const { pid } = useParams();
    const isNew = !pid;
    const navigate = useNavigate();
    const { api, site, siteId, currency, shop } = useSite();
    const unit = shop?.weight_unit || 'kg';
    const collections = useCollections();
    const [p, setP] = useState(isNew ? EMPTY : null);
    const [dirty, setDirty] = useState(false);
    useLeaveGuard(dirty);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const [handleTouched, setHandleTouched] = useState(!isNew);
    const [uploading, setUploading] = useState(0);
    const [uploadPct, setUploadPct] = useState(0);
    const [picking, setPicking] = useState(false);
    // images picked before the first save of a new product: shown right away, uploaded after the save
    const [queued, setQueued] = useState([]); // [{ key, src, file } | { key, src, media }]
    const initFor = useRef(null);

    useEffect(() => {
        // initialise once per product: a later re-run (context refresh, slow loads) must not wipe what the user typed
        const k = pid || 'new';
        if (initFor.current === k) return undefined;
        initFor.current = k;
        if (isNew) { setP(EMPTY); setDirty(false); setQueued([]); return undefined; }
        const live = () => initFor.current === k; // a newer product id wins; re-runs for the same id keep this request
        api.get(`/products/${pid}`).then((d) => { if (live()) { setP({ ...EMPTY, ...d, seo: { ...EMPTY.seo, ...(d?.seo || {}) } }); setDirty(false); } })
            .catch((e) => live() && setErr(e));
        return undefined;
    }, [api, pid, isNew]);

    useEffect(() => {
        if (!dirty) return undefined;
        const h = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [dirty]);

    const set = (patch) => { setP((x) => ({ ...x, ...(typeof patch === 'function' ? patch(x) : patch) })); setDirty(true); };
    const setOptions = (options) => set((x) => ({ options, variants: buildVariants(options, x.variants) }));
    const setVariant = (i, patch) => set((x) => ({ variants: x.variants.map((v, j) => (j === i ? { ...v, ...patch } : v)) }));
    const patchVariants = (idx, patch) => { const on = new Set(idx); set((x) => ({ variants: x.variants.map((v, j) => (on.has(j) ? { ...v, ...patch } : v)) })); };

    const hasOptions = (p?.options || []).some((o) => o.name && o.values?.length);

    const save = async () => {
        if (!p.title.trim()) { message.error(t('Title is required')); return; }
        setSaving(true);
        const body = {
            title: p.title.trim(), handle: p.handle, description_html: p.description_html, vendor: p.vendor, product_type: p.product_type, tax_class: p.tax_class || 'standard', ships_in_days: p.ships_in_days ?? null,
            tags: p.tags, status: p.status, seo: p.seo, collection_ids: p.collection_ids,
            options: p.options.filter((o) => o.name && o.values?.length),
            variants: p.variants.map((v) => ({
                ...(v.id ? { id: v.id } : {}), options: v.options || [], title: v.title, price: v.price ?? 0, compare_at_price: v.compare_at_price ?? null,
                cost: v.cost ?? null, sku: v.sku || '', barcode: v.barcode || '', weight: v.weight || 0, requires_shipping: !!v.requires_shipping,
                taxable: !!v.taxable, track_inventory: !!v.track_inventory, inventory_policy: v.inventory_policy || 'deny',
                inventory_quantity: Number(v.inventory_quantity) || 0, image_id: v.image_id || null,
            })),
        };
        try {
            const d = isNew ? await api.post('/products', body) : await api.put(`/products/${pid}`, body);
            message.success(t('Saved'));
            setDirty(false);
            if (isNew && d?.id) {
                // queued images picked before the first save
                const items = queued;
                navigate(sitePath(siteId, `products/${d.id}`), { replace: true });
                for (const q of items) { // eslint-disable-next-line no-await-in-loop, no-use-before-define
                    if (q.file) await upload(q.file, d.id); else await fromLibrary(q.media, d.id); // eslint-disable-line no-await-in-loop, no-use-before-define
                }
                setQueued([]);
                if (items.length) {
                    try { const fresh = await api.get(`/products/${d.id}`); setP((x) => ({ ...x, images: fresh?.images || x.images })); } catch (_) { /* thumbnails already added */ }
                }
            } else if (d) {
                setP((x) => ({ ...x, ...d, seo: { ...EMPTY.seo, ...(d.seo || {}) } }));
            }
        } catch (e) { toastErr(e, t('Save failed')); } finally { setSaving(false); }
    };

    const upload = async (file, productId = pid) => {
        if (!productId) { setQueued((q) => [...q, { key: `f${Date.now()}${Math.random()}`, src: URL.createObjectURL(file), file }]); setDirty(true); return; }
        setUploading((n) => n + 1);
        setUploadPct(0);
        try {
            const { key } = await directUpload(api, file, 'product_image', { onProgress: setUploadPct });
            const img = await api.post(`/products/${productId}/images`, { key });
            const one = img?.image || img;
            if (one?.id) setP((x) => ({ ...x, images: [...(x.images || []), one] }));
        } catch (e) { if (!reportStorageError(e)) toastErr(e, t('Upload failed')); } finally { setUploading((n) => n - 1); }
    };
    /** library pick: the product shares the stored file (nothing is copied) */
    const fromLibrary = async (m, productId = pid) => {
        if (!productId) { setQueued((q) => [...q, { key: `m${m.id}${Date.now()}`, src: m.url, media: m }]); setDirty(true); return; }
        try {
            const one = await api.post(`/products/${productId}/images`, { media_id: m.id });
            if (one?.id) setP((x) => ({ ...x, images: (x.images || []).some((i) => i.id === one.id) ? x.images : [...(x.images || []), one] }));
        } catch (e) { toastErr(e); }
    };
    const removeImage = async (img) => {
        try {
            await api.del(`/products/${pid}/images/${img.id}`);
            setP((x) => ({ ...x, images: x.images.filter((i) => i.id !== img.id), variants: x.variants.map((v) => (v.image_id === img.id ? { ...v, image_id: null } : v)) }));
        } catch (e) { toastErr(e); }
    };
    const reorderImages = async (images) => {
        setP((x) => ({ ...x, images }));
        try { await api.put(`/products/${pid}/images/order`, { ids: images.map((i) => i.id) }); } catch (e) { toastErr(e); }
    };
    const setAlt = async (img, alt) => {
        setP((x) => ({ ...x, images: x.images.map((i) => (i.id === img.id ? { ...i, alt } : i)) }));
        try { await api.put(`/products/${pid}/images/${img.id}`, { alt }, { skipGlobalErrorToast: true }); } catch (_) { /* alt update is optional */ }
    };
    const dragProps = useDragList(p?.images || [], reorderImages);

    const remove = async () => {
        try { await api.del(`/products/${pid}`); message.success(t('Deleted')); setDirty(false); navigate(sitePath(siteId, 'products')); } catch (e) { toastErr(e); }
    };

    const manualCollections = useMemo(() => collections.filter((c) => c.kind !== 'smart'), [collections]);

    if (err) return <Result status={err.status === 404 ? '404' : 'error'} title={err.status === 404 ? t('Product not found') : (err.error || t('Failed to load'))} extra={<Button onClick={() => navigate(sitePath(siteId, 'products'))}>{t('Products', { context: 'shop' })}</Button>} />;
    if (!p) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;

    const base = siteUrl(site);
    const productUrl = p.handle && base ? `${base}/products/${p.handle}` : '';
    const single = p.variants[0] || newVariant();

    const variantMore = (v, i) => (
        <Row gutter={[12, 8]}>
            <Col xs={12} md={6}><Text type="secondary">{t('Cost per item')}</Text><MoneyInput size="small" currency={currency} value={v.cost} onChange={(x) => setVariant(i, { cost: x })} /></Col>
            <Col xs={12} md={6}><Text type="secondary">{t('Barcode')}</Text><Input size="small" value={v.barcode} onChange={(e) => setVariant(i, { barcode: e.target.value })} /></Col>
            <Col xs={12} md={6}><Text type="secondary">{t('Weight', { context: 'shop' })} ({unit})</Text><InputNumber size="small" min={0} value={toUnit(v.weight, unit)} onChange={(x) => setVariant(i, { weight: toGrams(x, unit) })} style={{ width: '100%' }} /></Col>
            <Col xs={12} md={6}>
                <Text type="secondary">{t('Image', { context: 'shop' })}</Text>
                <Select size="small" allowClear style={{ width: '100%' }} value={v.image_id || undefined} onChange={(x) => setVariant(i, { image_id: x || null })}
                    options={(p.images || []).map((img, k) => ({ value: img.id, label: <Space><img src={img.src} alt="" style={{ width: 20, height: 20, objectFit: 'cover' }} />#{k + 1}</Space> }))} />
            </Col>
            <Col span={24}>
                <Space wrap>
                    <Checkbox checked={v.track_inventory} onChange={(e) => setVariant(i, { track_inventory: e.target.checked })}>{t('Track quantity')}</Checkbox>
                    <Checkbox checked={v.inventory_policy === 'continue'} onChange={(e) => setVariant(i, { inventory_policy: e.target.checked ? 'continue' : 'deny' })}>{t('Continue selling when out of stock')}</Checkbox>
                    <Checkbox checked={v.requires_shipping} onChange={(e) => setVariant(i, { requires_shipping: e.target.checked })}>{t('Physical product')}</Checkbox>
                    <Checkbox checked={v.taxable} onChange={(e) => setVariant(i, { taxable: e.target.checked })}>{t('Charge tax')}</Checkbox>
                </Space>
            </Col>
        </Row>
    );

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(sitePath(siteId, 'products'))} />
                <Text strong style={{ fontSize: 18 }}>{isNew ? t('Add product') : p.title || t('Product', { context: 'shop' })}</Text>
                {!isNew && <StatusTag value={p.status} />}
                {!isNew && productUrl && p.status === 'active' && <a href={productUrl} target="_blank" rel="noopener noreferrer"><ExportOutlined /> {t('View on site')}</a>}
                <div style={{ flex: 1 }} />
                <ShopAiButton kind="product" body={isNew ? {} : { product_id: Number(pid) }}
                    onResult={(r) => set((x) => {
                        const m = mergeAi(x, r, ['title', 'description_html', 'vendor', 'product_type', 'tags', 'seo']);
                        if (!m.description_html && r.description) m.description_html = r.description;
                        if (m.title && !handleTouched) m.handle = slugify(m.title);
                        return m;
                    })} />
            </div>
            <Row gutter={16}>
                <Col xs={24} lg={16}>
                    <Card style={{ marginBottom: 16 }}>
                        <Form layout="vertical">
                            <Form.Item label={t('Title')} required>
                                <Input value={p.title} maxLength={255} placeholder={t('Short sleeve t-shirt')}
                                    onChange={(e) => set({ title: e.target.value, ...(handleTouched ? {} : { handle: slugify(e.target.value) }) })} />
                            </Form.Item>
                            <Form.Item label={t('Description')} style={{ marginBottom: 0 }}>
                                <RichEditor value={p.description_html} onChange={(v) => set({ description_html: v })} />
                            </Form.Item>
                        </Form>
                    </Card>

                    <Card title={t('Media')} style={{ marginBottom: 16 }} extra={uploading > 0 ? <Space size={6}><Spin size="small" /><span style={{ fontSize: 12 }}>{uploadPct}%</span></Space> : null}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            {(p.images || []).map((img, i) => (
                                <div key={img.id} {...dragProps(i)} style={{ ...dragProps(i).style, width: 120 }}>
                                    <div style={{ position: 'relative', width: 120, height: 120, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ant-color-border, #eee)' }}>
                                        <img src={img.src} alt={img.alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
                                        <Popconfirm title={t('Delete this image?', { context: 'shop' })} onConfirm={() => removeImage(img)}>
                                            <Button size="small" danger icon={<DeleteOutlined />} style={{ position: 'absolute', top: 4, right: 4 }} />
                                        </Popconfirm>
                                    </div>
                                    <Input size="small" placeholder={t('Alt text')} defaultValue={img.alt} onBlur={(e) => e.target.value !== img.alt && setAlt(img, e.target.value)} style={{ marginTop: 4 }} />
                                </div>
                            ))}
                            {queued.map((q) => (
                                <div key={q.key} style={{ position: 'relative', width: 120, height: 120, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ant-color-border, #eee)' }}>
                                    <img src={q.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
                                    <Button size="small" danger icon={<DeleteOutlined />} style={{ position: 'absolute', top: 4, right: 4 }}
                                        onClick={() => setQueued((xs) => xs.filter((x) => x.key !== q.key))} />
                                </div>
                            ))}
                            <Upload accept="image/*" multiple showUploadList={false} beforeUpload={(f) => { upload(f); return false; }}>
                                <div style={{ width: 120, height: 120, border: '1px dashed var(--ant-color-border, #d9d9d9)', borderRadius: 8, display: 'grid', placeItems: 'center', cursor: 'pointer', textAlign: 'center' }}>
                                    <div><UploadOutlined style={{ fontSize: 20 }} /><div style={{ fontSize: 12 }}>{t('Add images')}</div></div>
                                </div>
                            </Upload>
                            <div onClick={() => setPicking(true)} style={{ width: 120, height: 120, border: '1px dashed var(--ant-color-border, #d9d9d9)', borderRadius: 8, display: 'grid', placeItems: 'center', cursor: 'pointer', textAlign: 'center' }}>
                                <div><PictureOutlined style={{ fontSize: 20 }} /><div style={{ fontSize: 12 }}>{t('From library')}</div></div>
                            </div>
                            <MediaPickerModal open={picking} siteId={siteId} onClose={() => setPicking(false)} onPick={fromLibrary} />
                        </div>
                    </Card>

                    {!hasOptions && (
                        <Card title={t('Pricing & inventory')} style={{ marginBottom: 16 }}>
                            <Row gutter={[16, 12]}>
                                <Col xs={24} sm={8}><Text type="secondary">{t('Price')}</Text><MoneyInput currency={currency} value={single.price} onChange={(v) => setVariant(0, { price: v ?? 0 })} /></Col>
                                <Col xs={24} sm={8}><Text type="secondary">{t('Compare-at price')}<Hint title={t('Shown crossed out to mark a sale')} /></Text><MoneyInput currency={currency} value={single.compare_at_price} onChange={(v) => setVariant(0, { compare_at_price: v })} /></Col>
                                <Col xs={24} sm={8}><Text type="secondary">{t('SKU')}</Text><Input value={single.sku} onChange={(e) => setVariant(0, { sku: e.target.value })} /></Col>
                                <Col xs={24} sm={8}>
                                    <Text type="secondary">{t('Quantity')}</Text>
                                    <InputNumber disabled={!single.track_inventory} value={single.inventory_quantity} onChange={(v) => setVariant(0, { inventory_quantity: v ?? 0 })} style={{ width: '100%' }} />
                                </Col>
                                <Col span={24}>{variantMore(single, 0)}</Col>
                            </Row>
                        </Card>
                    )}

                    <Card title={t('Options')} style={{ marginBottom: 16 }}
                        extra={p.options.length < 3 && <Button size="small" icon={<PlusOutlined />} onClick={() => setOptions([...p.options, { name: p.options.length ? '' : t('Size'), values: [] }])}>{t('Add option')}</Button>}>
                        {p.options.length === 0 && <Text type="secondary">{t('Add options like size or color to create variants.')}</Text>}
                        {p.options.map((o, i) => (
                            <Row gutter={8} key={i} style={{ marginBottom: 8 }} align="middle">
                                <Col xs={24} sm={7}><Input placeholder={t('Option name')} value={o.name} onChange={(e) => setOptions(p.options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} /></Col>
                                <Col xs={20} sm={15}>
                                    <TagsInput format="array" spaceSeparates={false} value={o.values} placeholder={t('Values, separated by commas')}
                                        onChange={(vals) => setOptions(p.options.map((x, j) => (j === i ? { ...x, values: vals } : x)))} style={{ width: '100%' }} />
                                </Col>
                                <Col xs={4} sm={2}><Button type="text" danger icon={<DeleteOutlined />} onClick={() => setOptions(p.options.filter((_, j) => j !== i))} /></Col>
                            </Row>
                        ))}
                    </Card>

                    {hasOptions && (
                        <Card title={t('Variants')} style={{ marginBottom: 16 }}>
                            <ProductVariants options={p.options} variants={p.variants} images={p.images} currency={currency} unit={unit} onPatch={patchVariants} renderMore={variantMore} />
                        </Card>
                    )}

                    <Collapse style={{ marginBottom: 16 }} items={[{
                        key: 'seo',
                        label: t('Search engine listing'),
                        children: (
                            <Form layout="vertical">
                                <div style={{ marginBottom: 12, padding: 12, background: 'var(--ant-color-fill-quaternary, #fafafa)', borderRadius: 6 }}>
                                    <div style={{ color: '#1a0dab', fontSize: 16 }}>{p.seo.title || p.title || t('Product title')}</div>
                                    <div style={{ color: '#006621', fontSize: 12 }}>{(base || '').replace(/^https?:\/\//, '')}/products/{p.handle || slugify(p.title)}</div>
                                    <div style={{ fontSize: 13, color: '#545454' }}>{p.seo.description || t('Add a description to see how this product might appear in search results.')}</div>
                                </div>
                                <Form.Item label={t('Page title')}><Input value={p.seo.title} maxLength={255} showCount onChange={(e) => set({ seo: { ...p.seo, title: e.target.value } })} /></Form.Item>
                                <Form.Item label={t('Meta description')}><Input.TextArea value={p.seo.description} maxLength={500} showCount autoSize={{ minRows: 2 }} onChange={(e) => set({ seo: { ...p.seo, description: e.target.value } })} /></Form.Item>
                                <Form.Item label={t('URL handle')} style={{ marginBottom: 0 }}>
                                    <Input addonBefore="/products/" value={p.handle} onChange={(e) => { setHandleTouched(true); set({ handle: slugify(e.target.value) }); }} />
                                </Form.Item>
                            </Form>
                        ),
                    }]} />
                    {!isNew && <CustomFieldsCard owner="product" oid={pid} />}
                    {!isNew && <TranslationsCard type="product" oid={pid} />}
                    {!isNew && <ProductDigitalFiles productId={pid} variants={p.variants} />}
                </Col>

                <Col xs={24} lg={8}>
                    <Card title={t('Status')} style={{ marginBottom: 16 }}>
                        <Select value={p.status} onChange={(v) => set({ status: v })} style={{ width: '100%' }}
                            options={[{ value: 'active', label: t('Active') }, { value: 'draft', label: t('Draft') }, { value: 'archived', label: t('Archived') }]} />
                    </Card>
                    <Card title={t('Product organization')} style={{ marginBottom: 16 }}>
                        <Form layout="vertical">
                            <Form.Item label={t('Product type', { context: 'shop' })}><Input value={p.product_type} onChange={(e) => set({ product_type: e.target.value })} /></Form.Item>
                            <Form.Item label={t('Vendor')}><Input value={p.vendor} onChange={(e) => set({ vendor: e.target.value })} /></Form.Item>
                            <Form.Item label={t('Tax class')}><Select value={p.tax_class || 'standard'} onChange={(v) => set({ tax_class: v })} options={[{ value: 'standard', label: t('Standard rate') }, { value: 'reduced', label: t('Reduced rate') }, { value: 'zero', label: t('Zero rate') }]} /></Form.Item>
                            <Form.Item label={t('Ships in (days)')} tooltip={t('Processing time before shipping, in business days')}><InputNumber min={0} max={365} precision={0} style={{ width: '100%' }} value={p.ships_in_days ?? null} onChange={(v) => set({ ships_in_days: v })} /></Form.Item>
                            <Form.Item label={t('Collections')}>
                                <Select mode="multiple" value={p.collection_ids} onChange={(v) => set({ collection_ids: v })} optionFilterProp="label"
                                    options={manualCollections.map((c) => ({ value: c.id, label: c.title }))}
                                    notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No manual collections')} />} />
                            </Form.Item>
                            <Form.Item label={t('Tags')} style={{ marginBottom: 0 }}>
                                <TagsInput format="array" spaceSeparates={false} value={p.tags} onChange={(v) => set({ tags: v })} />
                            </Form.Item>
                        </Form>
                    </Card>
                    {!isNew && (
                        <Card size="small">
                            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                <Text type="secondary">{t('Sold')}: {p.sold_count ?? 0}</Text>
                                <Popconfirm title={t('Delete this product?')} okButtonProps={{ danger: true }} onConfirm={remove}>
                                    <Button danger icon={<DeleteOutlined />}>{t('Delete product')}</Button>
                                </Popconfirm>
                            </Space>
                        </Card>
                    )}
                </Col>
            </Row>
            <Affix offsetBottom={0}>
                <div style={{
                    display: dirty || isNew ? 'flex' : 'none', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', marginTop: 8,
                    background: 'var(--ant-color-bg-elevated, #fff)', borderTop: '1px solid var(--ant-color-border, #eee)', boxShadow: '0 -2px 8px rgba(0,0,0,.06)',
                }}>
                    <Text type="secondary" style={{ marginRight: 'auto', alignSelf: 'center' }}>{dirty ? t('Unsaved changes') : ''}</Text>
                    <Button onClick={() => { setDirty(false); navigate(sitePath(siteId, 'products')); }}>{dirty || !isNew ? t('Discard') : t('Cancel')}</Button>
                    <Button type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
                </div>
            </Affix>
        </div>
    );
};

export default ProductEdit;
